import React, { useState, useEffect } from 'react';
import { Chess } from 'chess.js';
import { PIECE_IMAGES } from '../../utils/AppConstants';

const parseFenToBoard = (fen) => {
    const board = {};
    if (!fen) return board;
    const rows = fen.split(' ')[0].split('/');
    const cols = ['a','b','c','d','e','f','g','h'];
    rows.forEach((row, rIdx) => {
        let cIdx = 0;
        for(let char of row) {
            if(!isNaN(char)) cIdx += parseInt(char);
            else { board[`${cols[cIdx]}${8-rIdx}`] = { type: char.toLowerCase(), color: char === char.toUpperCase() ? 'w' : 'b' }; cIdx++; }
        }
    });
    return board;
};

export default function DeepReviewBoard({ submission, onClose }) {
    const [selectedIdx, setSelectedIdx] = useState(0);
    const [currentFen, setCurrentFen] = useState("");

    // Identify log type
    const isBootcamp = submission?.gameType?.startsWith('BOOTCAMP_');
    const isAppleCapture = submission?.gameType === 'APPLE_CAPTURE';
    const isMcqQuiz = submission?.gameType === 'MCQ_QUIZ';
    const isTactics = !isBootcamp && !isAppleCapture && !isMcqQuiz;

    const activeLog = (isTactics || isBootcamp) ? submission?.logs?.[selectedIdx] : null;

    useEffect(() => {
        if (activeLog) {
            if (activeLog.startingFen || activeLog.fen) {
                setCurrentFen(activeLog.startingFen || activeLog.fen);
            } else {
                setCurrentFen("");
            }
        } else {
            setCurrentFen("");
        }
    }, [activeLog]);

    const displayBoard = parseFenToBoard(currentFen);
    const playerTurn = (currentFen || '8/8/8/8/8/8/8/8 w').split(' ')[1] || 'w';
    const isFlipped = playerTurn === 'b';
    const rows = isFlipped ? [1,2,3,4,5,6,7,8] : [8,7,6,5,4,3,2,1]; 
    const cols = isFlipped ? ['h','g','f','e','d','c','b','a'] : ['a','b','c','d','e','f','g','h'];

    const previewMove = (from, to) => {
        if (!currentFen) return;
        try {
            const game = new Chess(currentFen);
            game.move({from, to, promotion: 'q'});
            setCurrentFen(game.fen());
        } catch(e) {}
    };

    const resetBoard = () => {
        if (activeLog && (activeLog.startingFen || activeLog.fen)) {
            setCurrentFen(activeLog.startingFen || activeLog.fen);
        }
    };

    // Calculate mistakes for header display
    const getMistakesCount = () => {
        if (isAppleCapture || isMcqQuiz) {
            return submission.logs.filter(l => !l.isCorrect).length;
        }
        if (isBootcamp) {
            let count = 0;
            (submission.logs || []).forEach(log => {
                count += log.attempts?.filter(a => !a.isCorrect).length || 0;
            });
            return count;
        }
        // Tactics
        let count = 0;
        (submission.logs || []).forEach(log => {
            count += log.attempts?.filter(a => !a.isCorrect).length || 0;
        });
        return count;
    };

    const mistakesCount = getMistakesCount();

    return (
        <div style={{background: 'var(--bg-primary)', minHeight: '85vh', padding: '30px', borderRadius: '20px', border: '1px solid var(--border-glass)', position: 'relative', boxShadow: 'var(--panel-shadow)', transition: 'background-color 0.3s ease' }}>
            
            {/* Header bar */}
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', gap: '15px', flexWrap: 'wrap' }}>
                <button onClick={onClose} className="neu-btn" style={{padding: '10px 20px', fontWeight: 'bold' }}>⬅ Back to Tracker</button>
                <div style={{color: 'var(--text-primary)', fontSize: '20px', fontWeight: 'bold' }}>
                    🔍 Reviewing Homework: <span style={{color: '#3b82f6'}}>{submission.studentName}</span>
                </div>
                <div style={{background: mistakesCount === 0 ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', border: mistakesCount === 0 ? '1px solid #10b981' : '1px solid #ef4444', padding: '8px 16px', borderRadius: '10px', color: mistakesCount === 0 ? '#10b981' : '#ef4444', fontWeight: 'bold', fontSize: '14px' }}>
                    {mistakesCount === 0 ? '🎯 Perfect Score (0 Errors)' : `⚠️ Total Errors: ${mistakesCount}`}
                </div>
            </div>

            {/* Task Index Selector for multi-step tasks */}
            {(isTactics || isBootcamp) && (
                <div style={{display: 'flex', gap: '8px', overflowX: 'auto', marginBottom: '35px', background: 'var(--bg-secondary)', padding: '12px', borderRadius: '12px', border: '1px solid var(--border-glass)' }}>
                    {(submission.logs || []).map((log, idx) => {
                        const hasMistakes = log.attempts?.some(a => !a.isCorrect);
                        const isSelected = selectedIdx === idx;
                        return (
                            <button 
                                key={idx} 
                                onClick={() => setSelectedIdx(idx)}
                                className={isSelected ? 'neu-btn-primary' : 'neu-btn'}
                                style={{minWidth: '45px', 
                                    height: '45px', 
                                    borderRadius: '10px', 
                                    fontWeight: 'bold', 
                                    padding: 0,
                                    fontSize: '15px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    background: isSelected ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' : (hasMistakes ? 'rgba(239, 68, 68, 0.15)' : 'rgba(16, 185, 129, 0.15)'),
                                    borderColor: isSelected ? '#ffffff' : (hasMistakes ? '#ef4444' : '#10b981'),
                                    color: isSelected ? '#ffffff' : (hasMistakes ? '#ef4444' : '#10b981'),
                                    boxShadow: isSelected ? '0 4px 10px rgba(59, 130, 246, 0.3)' : 'none'
                                }}
                            >
                                {isBootcamp ? (idx + 1) : (idx + 1)}
                            </button>
                        );
                    })}
                </div>
            )}

            <div style={{display: 'flex', gap: '40px', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'flex-start' }}>
                
                {/* 1. Chessboard preview or minigame layout */}
                {currentFen ? (
                    <div style={{width: '450px', maxWidth: '100%', border: '4px solid var(--border-glass)', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' }}>
                        <div style={{width: '100%', aspectRatio: '1/1', display: 'grid', gridTemplateColumns: `repeat(8, 1fr)`, gridTemplateRows: `repeat(8, 1fr)` }}>
                            {rows.map((row, rIdx) => cols.map((col, cIdx) => {
                                const id = `${col}${row}`; 
                                const isDark = (rIdx + cIdx) % 2 === 1;
                                const pieceObj = displayBoard[id];
                                return (
                                    <div key={id} style={{position: 'relative', background: isDark ? '#769656' : '#eeeed2', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        {cIdx === 0 && <span style={{position: 'absolute', top: 2, left: 3, fontSize: '11px', color: isDark ? '#eeeed2' : '#769656', fontWeight: 'bold', pointerEvents: 'none', zIndex: 5 }}>{row}</span>}
                                        {rIdx === 7 && <span style={{position: 'absolute', bottom: 1, right: 3, fontSize: '11px', color: isDark ? '#eeeed2' : '#769656', fontWeight: 'bold', pointerEvents: 'none', zIndex: 5 }}>{col}</span>}
                                        {pieceObj && <img src={PIECE_IMAGES[pieceObj.color + pieceObj.type.toUpperCase()]} alt="piece" style={{width: '90%', zIndex: 10, filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.4))' }} />}
                                    </div>
                                );
                            }))}
                        </div>
                    </div>
                ) : (
                    // Fallback visual icon for non-fen minigames
                    <div style={{width: '450px', maxWidth: '100%', background: 'var(--bg-secondary)', padding: '50px', borderRadius: '16px', border: '1px dashed var(--border-glass)', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '350px' }}>
                        <div style={{fontSize: '90px', marginBottom: '20px' }}>
                            {isAppleCapture ? '🍎' : isMcqQuiz ? '🧩' : '🤖'}
                        </div>
                        <h3 style={{color: 'var(--text-primary)', margin: '0 0 10px 0', fontSize: '22px' }}>
                            {isAppleCapture ? 'Apple Capture Bootcamp' : isMcqQuiz ? 'Piece Names Quiz' : 'Bootcamp Quiz'}
                        </h3>
                        <p style={{color: 'var(--text-secondary)', fontSize: '14px', maxWidth: '320px', lineHeight: '1.6' }}>
                            This classroom puzzle was completed in the interactive bootcamp. View the detailed interaction logs on the right panel.
                        </p>
                    </div>
                )}

                {/* 2. Detailed attempt history timeline */}
                <div style={{flex: 1, minWidth: '320px', background: 'var(--bg-secondary)', padding: '30px', borderRadius: '20px', border: '1px solid var(--border-glass)', boxShadow: 'var(--panel-shadow)' }}>
                    
                    {activeLog ? (
                        <>
                            <div style={{background: '#3b82f6', color: 'var(--text-primary)', display: 'inline-block', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold', marginBottom: '15px' }}>
                                Task {selectedIdx + 1}
                            </div>
                            
                            <h3 style={{color: 'var(--text-primary)', margin: '0 0 10px 0', fontSize: '22px' }}>
                                {activeLog.theme || activeLog.question || 'Challenge Position'}
                            </h3>
                            
                            <div style={{display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '20px' }}>
                                <div style={{color: 'var(--text-secondary)', fontSize: '12px', textTransform: 'uppercase', fontWeight: 'bold', letterSpacing: '0.5px' }}>Interactive Moves & Clicks:</div>
                                {activeLog.attempts && activeLog.attempts.length > 0 ? (
                                    activeLog.attempts.map((att, i) => (
                                        <div 
                                            key={i} 
                                            onMouseEnter={() => {
                                                if (att.from && att.to) previewMove(att.from, att.to);
                                            }}
                                            onMouseLeave={resetBoard}
                                            style={{background: att.isCorrect ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)', 
                                                border: att.isCorrect ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(239, 68, 68, 0.3)', 
                                                padding: '12px 18px', 
                                                borderRadius: '10px', 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: '12px', 
                                                cursor: (att.from && att.to) ? 'pointer' : 'default',
                                                transition: '0.2s' 
                                            }}
                                        >
                                            <div style={{fontSize: '18px' }}>{att.isCorrect ? '✅' : '❌'}</div>
                                            <div>
                                                <div style={{color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '15px' }}>
                                                    {att.played || att.san || `${att.from} ➔ ${att.to}`}
                                                </div>
                                                <div style={{color: 'var(--text-secondary)', fontSize: '11px' }}>
                                                    {att.isCorrect ? 'Correct Path' : 'Incorrect attempt'}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div style={{color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: '13px' }}>Completed successfully without mistakes.</div>
                                )}
                            </div>
                        </>
                    ) : (
                        // Flat logs list for single-page minigames
                        <>
                            <div style={{background: '#f59e0b', color: 'black', display: 'inline-block', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold', marginBottom: '15px' }}>
                                Flat Timeline Review
                            </div>
                            
                            <h3 style={{color: 'var(--text-primary)', margin: '0 0 10px 0', fontSize: '22px' }}>
                                {isAppleCapture ? '🍎 Apple Capture Logs' : '🧩 MCQ Piece Quiz Logs'}
                            </h3>
                            
                            <div style={{display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '20px', maxHeight: '400px', overflowY: 'auto', paddingRight: '5px' }}>
                                {submission.logs && submission.logs.length > 0 ? (
                                    submission.logs.map((log, i) => (
                                        <div 
                                            key={i} 
                                            style={{background: log.isCorrect ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)', 
                                                border: log.isCorrect ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(239, 68, 68, 0.3)', 
                                                padding: '12px 18px', 
                                                borderRadius: '10px', 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: '12px'
                                            }}
                                        >
                                            <div style={{fontSize: '18px' }}>{log.isCorrect ? '✅' : '❌'}</div>
                                            <div>
                                                <div style={{color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '14px' }}>
                                                    {log.move || `Answer: ${log.selectedAnswer}`}
                                                </div>
                                                <div style={{color: 'var(--text-secondary)', fontSize: '11px' }}>
                                                    {isMcqQuiz ? `Question Piece: ${log.questionPiece}` : (log.isCorrect ? 'Correct move' : 'Illegal attempt')}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div style={{color: 'var(--text-secondary)', fontStyle: 'italic', fontSize: '13px' }}>No logs registered for this game.</div>
                                )}
                            </div>
                        </>
                    )}

                </div>
            </div>
        </div>
    );
}
